import * as mobx from 'mobx';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import Rollbar from 'rollbar';

import { RESOLVER } from './adl-gen/resolver';
import { createJsonBinding } from './adl-gen/runtime/json';
import { texprUiConfig, UiConfig } from './adl-gen/totalinteraction/futurefit/uiconfig';
import { App } from './app/app';
import { Auth, AuthEventSource } from './auth';
import { BrowserHttp } from './hx/service/browser-http';
import { HttpWithRetries } from './hx/service/retrying-http';
import { I18n, withI18n } from './internationalization/i18n-hoc';
// // tslint:disable-next-line: no-default-import
// import i18next from "./internationalization/i18next";
import { MaybeLoading } from './loader/loader';
import { Router, WindowSubset } from './routing/router';
import { Service } from './service/service';
import { ServiceError } from './service/service-error';
import { AppStore } from './stores/app-store';
import { IdentityService, MaybeLoggedIn } from './stores/identity-store';
import { StoreFactory } from './stores/store-factory';
// tslint:disable-next-line: no-import-side-effect
import './ui/common/init-global-css';
import { assertNever } from './util/types';

declare const window: Window & {
  /** UI config JSON injected via index.html */
  UI_CONFIG?: {};
} & WindowSubset;

// Fetch and parse the typed UiConfig record from the window
// This has been populated via a script tag in index.html
const UI_CONFIG: UiConfig = (() => {
  if (!window.UI_CONFIG) {
    throw new Error('UI_CONFIG not populated');
  }
  try {
    return createJsonBinding(RESOLVER, texprUiConfig()).fromJson(window.UI_CONFIG);
  } catch (e) {
    throw new Error('Could not parse UI_CONFIG: ' + e);
  }
})();

// tslint:disable:no-console
console.info('Loading skillseeker');
console.info(`Environment: ${UI_CONFIG.environment}`);
console.info(`Release name: ${UI_CONFIG.releaseName}`);
// tslint:enable:no-console

// Load Rollbar
// NOTE: We are following the NodeJS style configuration as opposed to the
// NOTE: browser JS style because we do not want to load it via index.html and also
// NOTE: have a handle on the created Rollbar object.
// NOTE: See: https://docs.rollbar.com/docs/nodejs
const rollbar = (function (): Rollbar | undefined {
  if (UI_CONFIG.rollbar) {
    return new Rollbar({
      accessToken: UI_CONFIG.rollbar.accessToken,
      payload: {
        environment: UI_CONFIG.environment
      },

      // We register our own error handlers and send to Rollbar manually
      captureUncaught: false,
      captureUnhandledRejections: false
    });
  } else {
    return undefined;
  }
})();

// Global error handler, includes errors that occur in the handler
window.addEventListener('error', (event: ErrorEvent) => handleError('global error', event.error, event));

// Global unhandled rejection handler
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) =>
  handleError('unhandled rejection', event.reason, event)
);

/**
 * Last resort error handler that alerts the user.
 */
function handleError(
  eventType: 'global error' | 'unhandled rejection',
  error: { publicMessage?: string },
  event: Event
) {
  // tslint:disable-next-line:no-console
  console.error(`${eventType}: ${error}, error:`, error, 'event:', event);
  if (rollbar) {
    rollbar.error(
      '' + error, // message
      error, // exception object
      { eventType, event } // custom payload
    );
  }
  if (error.publicMessage) {
    window.alert(error.publicMessage);
  } else {
    window.alert('Sorry, an unhandled error has occurred. Please try refreshing and performing the action again.');
  }
}

mobx.configure({
  // Enforce state changes to occur with a method decorated by @action or runInAction
  enforceActions: 'observed'
});

// Manage login handling
const origin = window.location.origin;
const auth = new Auth(origin, UI_CONFIG.auth0);

const router = new Router(location.pathname, window.history, window);

// Configure http to retry up to 8 times, with an initial delay of 100ms, with
// exponential increases.  The precise delays are randomised, but this will
// result in a typical max delay before failure of 25 seconds
const http = new HttpWithRetries(new BrowserHttp(), 8, 100);

const service = new Service(http, '/_a', RESOLVER, auth);

const storeFactory = new StoreFactory(service);

const i18n = I18n.getInstance(service);

const appStore = new AppStore(auth, router, storeFactory, i18n);

const identityService = new IdentityService(service, UI_CONFIG.appClient, (identity: MaybeLoading<MaybeLoggedIn>, context?: string) => {
  type IdType = 'loading' | 'logged-in' | 'super-logged-in' | 'not-logged-in' | 'unregistered' | 'error';
  appStore.identity = identity;
  let idType: IdType = 'loading';
  switch (identity.kind) {
    case 'loading': idType = identity.kind; break;
    case 'value': idType = identity.value.kind; break;
    case 'error': idType = identity.kind; break;
    default: assertNever(identity, 'Unknown identity type');
  }
  // tslint:disable-next-line: no-console
  console.info(context, 'identityService callback setIdentity: ', idType, ' calling onRouteChange');

  appStore.onRouteChange(context);
});

auth.setOnAuthEvent((isLoggedIn: boolean, source: AuthEventSource, context?: string) => {
  // tslint:disable-next-line: no-console
  console.info('Auth onAuthEvent callback isLoggedIn=', isLoggedIn, ' source', source);

  /// After handling the auth0 callback we need to clean up the path and hash:
  if (source === 'handledAuthRedir') {
    // tslint:disable-next-line: no-console
    console.info('Auth onAuthEvent callback handleAuthRedir navigating to home');
    appStore.navigateTo({ route: 'home' });
  }

  appStore.onRouteChange(context);
});

service.setErrorHandler((error: ServiceError) => {
  // Check if it's an authorization error
  if (error.status === 401) {
    // DEV:
    // tslint:disable-next-line: no-console
    console.error('Auth error response', error.status);
    return;

    /*
      const publicMessageFragment = error.publicMessage
        ? `: ${error.publicMessage}`
        : "";
      window.alert(
        `Sorry, an authorization error occurred ${publicMessageFragment}. You will be logged out.`
      );
      if (appStore) {
        appStore.navigateToLogout();
      }
  
      return;
      */
  }
});

/// Pass the service to Auth so that it can use /user/login to set a
/// server-issued token with claims needed by the API
auth.setService(service);
// Pass the router as well so that we can move all the code that deals with
// "/auth0-callback" into async code
auth.setRouter(router);

// After constructing the core components - do an async init() over each in a controlled order.
async function init() {
  // tslint:disable-next-line: no-console
  console.info('Start async init');

  // // tslint:disable-next-line: no-console
  // console.info("i18next.init() (called with await in init section)");
  // /// init: for all options read: https://www.i18next.com/overview/configuration-options
  // await i18next.init({
  //   fallbackLng: 'en',
  //   debug: true,

  //   interpolation: {
  //     escapeValue: false, // not needed for react as it escapes by default
  //   },
  // });

  // tslint:disable-next-line: no-console
  console.info(`i18n.init()`);
  await i18n.init();

  const AppWithI18n = withI18n(App);

  ReactDOM.render(<AppWithI18n store={appStore} uiconfig={UI_CONFIG} />, document.getElementById('root'));

  // tslint:disable-next-line: no-console
  console.info('router.init()');
  await router.init();

  // tslint:disable-next-line: no-console
  console.info('auth.init()');
  await auth.init();

  // tslint:disable-next-line: no-console
  console.info('service.init();');
  await service.init();

  // tslint:disable-next-line: no-console
  console.info('appStore.init()');
  await appStore.init();

  // tslint:disable-next-line: no-console
  console.info('identityService.init();');
  await identityService.init();

  //tslint:disable-next-line:no-console
  console.info('Async init done');
}

init().catch(err => {
  // tslint:disable-next-line: no-console
  console.error('failed top level async init', err);
});



// WEBPACK FOOTER //
// ./src/index.tsx